import {
  Accordion,
  Attribute,
  Button,
  CustomMobileAccordionType,
  DataTable,
  DataTableColumn,
  DataTableDataType,
  FormatterType,
  Icon,
  IconFormatter,
  MatchUp,
  StatsGame,
  StatsGameFormatter,
  Tag,
  Typography,
  formatGameDate,
} from "best-common-react";
import React from "react";
import { Link } from "react-router";
import styled from "styled-components";
import IconLinkFormatter from "../../../components/tables/IconLinkFormatter";

export const DAY_OF_REQUEST_STATUS_IDS = {
  FORWARDED: 1,
  REVOKED: 2,
  FAILED: 3,
  LOTTERY_PENDING: 4,
  LOTTERY_WON: 5,
  LOTTERY_LOST: 6,
  DELETED: 7,
  CANCELLED: 8,
  PENDING_FORWARD: 9,
};

const SUCCESS_STATUS_IDS = [
  DAY_OF_REQUEST_STATUS_IDS.FORWARDED,
  DAY_OF_REQUEST_STATUS_IDS.LOTTERY_WON,
  DAY_OF_REQUEST_STATUS_IDS.PENDING_FORWARD,
];
const DANGER_STATUS_IDS = [
  DAY_OF_REQUEST_STATUS_IDS.REVOKED,
  DAY_OF_REQUEST_STATUS_IDS.FAILED,
  DAY_OF_REQUEST_STATUS_IDS.LOTTERY_LOST,
  DAY_OF_REQUEST_STATUS_IDS.DELETED,
  DAY_OF_REQUEST_STATUS_IDS.CANCELLED,
];
const WARNING_STATUS_IDS = [DAY_OF_REQUEST_STATUS_IDS.LOTTERY_PENDING];

const getStatusVariant = (statusId?: number): React.ComponentProps<typeof Tag>["variant"] => {
  if (statusId && SUCCESS_STATUS_IDS.includes(statusId)) {
    return "success";
  }
  if (statusId && DANGER_STATUS_IDS.includes(statusId)) {
    return "danger";
  }
  if (statusId && WARNING_STATUS_IDS.includes(statusId)) {
    return "warning";
  }
  return "default";
};

export type DayOfTableData = DataTableDataType & {
  dayOfRequestId: number;
  firstName: string;
  lastName: string;
  ballparkEmail: string;
  numberOfTickets: number;
  game: StatsGame;
  ticketsSentDisplay: string;
  requestTime: string;
  editLink: string;
  status: string;
  statusId: number;
  stadium: string;
};

const StatusFormatter = ({ row, value }: FormatterType<DayOfTableData>) => (
  <Tag variant={getStatusVariant(row?.statusId)} label={value} />
);

const formatMobileGameDate = (game: StatsGame) =>
  formatGameDate(game.gameDate, game.venue?.timeZone, game.status?.startTimeTBD);

const MobileHeader = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  align-items: start;
  gap: var(--spacing-1);
  width: 100%;
`;

const TodaysTicketsAccordion = (
  onDeleteClicked?: (index: number, value?: DayOfTableData) => void,
  onRevokeClicked?: (value: DayOfTableData) => void,
  revokeStatusIds: number[] = [],
  hasEdit = false,
  hasDelete = false,
) => {
  const AccordionComponent = ({
    data,
    index,
    ...rest
  }: CustomMobileAccordionType<DayOfTableData> & { children?: React.ReactNode }) => {
    const {
      editLink,
      game,
      firstName,
      lastName,
      ballparkEmail,
      numberOfTickets,
      stadium,
      ticketsSentDisplay,
      status,
      statusId,
      requestTime,
    } = data as DayOfTableData;
    const canRevoke = !!onRevokeClicked && revokeStatusIds.includes(statusId);
    return (
      <Accordion {...rest}>
        <Accordion.Header>
          <MobileHeader>
            <div className="d-flex align-items-start overflow-hidden">
              {hasEdit && (
                <Link to={editLink}>
                  <Icon iconName="fa-pencil-alt" className="fa-lg bcr-me-3" />
                </Link>
              )}
              <div className="overflow-hidden">
                <Typography variant="bodyMedium" className="text-truncate">
                  {formatMobileGameDate(game)}
                </Typography>
                <Typography variant="bodyLarge" className="text-truncate">
                  {firstName} {lastName}
                </Typography>
              </div>
            </div>
            <div className="d-flex flex-column align-items-end bcr-gap-1">
              <MatchUp away={game.teams.away.team} home={game.teams.home.team} logoSize="24px" />
              <Tag variant={getStatusVariant(statusId)} label={status} />
            </div>
          </MobileHeader>
        </Accordion.Header>
        <Accordion.Body>
          <div>
            {hasDelete && (
              <div className="d-flex justify-content-end">
                <Icon iconName="fa-trash-alt" className="fa-lg" onClick={() => onDeleteClicked?.(index, data)} />
              </div>
            )}
            <Attribute header="Email:" value={ballparkEmail} horizontal headerWidth={145} />
            <Attribute header="Stadium:" value={stadium} horizontal headerWidth={145} />
            <Attribute header="# Tickets:" value={numberOfTickets?.toString()} horizontal headerWidth={145} />
            <Attribute header="Tickets Sent:" value={ticketsSentDisplay} horizontal headerWidth={145} />
            <Attribute header="Request Time:" value={requestTime} horizontal headerWidth={145} />
            {canRevoke && (
              <Button
                variant="default"
                size="sm"
                className="w-100 bcr-mt-2"
                onClick={() => onRevokeClicked?.(data as DayOfTableData)}
              >
                Revoke
              </Button>
            )}
          </div>
        </Accordion.Body>
      </Accordion>
    );
  };
  return AccordionComponent;
};

type TodaysTicketsDataTableProps = {
  requests: DayOfTableData[];
  hasEdit?: boolean;
  hasDelete?: boolean;
  hasRevoke?: boolean;
  onDeleteClicked?: (index: number, value?: DayOfTableData) => void;
  onRevokeClicked?: (value: DayOfTableData) => void;
  revokeStatusIds?: number[];
  loading?: boolean;
};

const TodaysTicketsDataTable = ({
  requests,
  hasDelete = false,
  hasEdit = false,
  hasRevoke = false,
  onDeleteClicked,
  onRevokeClicked,
  revokeStatusIds = [],
  loading = false,
}: TodaysTicketsDataTableProps) => {
  const RevokeFormatter = ({ row }: FormatterType<DayOfTableData>) =>
    row && revokeStatusIds.includes(row.statusId) ? (
      <Button variant="default" size="sm" onClick={() => onRevokeClicked?.(row)}>
        Revoke
      </Button>
    ) : null;

  const Cols: DataTableColumn<DayOfTableData>[] = [
    {
      key: "deleteLink",
      name: "",
      width: 35,
      onClick: onDeleteClicked,
      readonlyFormatter: IconFormatter,
      icon: "fa-trash-alt",
      fixed: true,
      visible: hasDelete,
    },
    {
      key: "dayOfRequestId",
      name: "",
      width: 90,
      readonlyFormatter: RevokeFormatter,
      fixed: true,
      visible: hasRevoke,
    },
    {
      key: "editLink",
      name: "",
      width: 35,
      readonlyFormatter: IconLinkFormatter,
      icon: "fa-pencil-alt",
      fixed: true,
      visible: hasEdit,
    },
    { key: "game", name: "Game", width: 350, readonlyFormatter: StatsGameFormatter, fixed: true },
    { key: "firstName", name: "First Name", minWidth: 100 },
    { key: "lastName", name: "Last Name", minWidth: 100 },
    { key: "status", name: "Status", minWidth: 145, readonlyFormatter: StatusFormatter },
    { key: "ballparkEmail", name: "Ballpark Email", minWidth: 200 },
    { key: "numberOfTickets", name: "# of Tickets", cellClass: "justify-content-center", width: 100 },
    { key: "stadium", name: "Stadium", minWidth: 120 },
    { key: "ticketsSentDisplay", name: "Tickets Sent", minWidth: 150 },
    { key: "requestTime", name: "Request Time", minWidth: 250 },
  ];

  return (
    <DataTable
      className="bcr-mb-3"
      data={requests}
      columns={Cols}
      statusTextOverride={(_selected, total) => `${total} Requests`}
      accordion={TodaysTicketsAccordion(onDeleteClicked, onRevokeClicked, revokeStatusIds, hasEdit, hasDelete)}
      tableHeights={{
        maxTableHeight: 600,
        minTableHeight: 300,
        autogrow: true,
      }}
      loading={loading}
    />
  );
};

export default TodaysTicketsDataTable;
